import React, { createContext, useContext, useState } from 'react';

const AuthContext = createContext();

const readStoredUser = () => {
  try {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('token');
    if (!savedUser || !savedToken) return null;

    const parsedUser = JSON.parse(savedUser);
    if (parsedUser.roleName && !parsedUser.role) {
      if (parsedUser.roleName === 'Manager' || parsedUser.roleName === 'manager') {
        parsedUser.role = 1;
      } else if (parsedUser.roleName === 'Conductor' || parsedUser.roleName === 'conductor') {
        parsedUser.role = 3;
      } else {
        parsedUser.role = 2;
      }
    }
    return parsedUser;
  } catch (error) {
    console.error('Error parsing saved user:', error);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readStoredUser);
  const [loading, setLoading] = useState(false);

  // User roles
  const ROLES = {
    MANAGER: 1,
    PASSENGER: 2,
    CONDUCTOR: 3
  };

  // Login function - handles password login for Manager and Conductor
  const login = async (credentials) => {
    try {
      setLoading(true);
      
      // Import authApi dynamically to avoid circular dependencies
      const { authApi } = await import('../services/api');
      
      // Call backend login API
      const response = await authApi.login(credentials.username, credentials.password);
      
      // Map role name to role ID
      let roleId = ROLES.PASSENGER;
      if (response.role === 'Manager') {
        roleId = ROLES.MANAGER;
      } else if (response.role === 'Conductor') {
        roleId = ROLES.CONDUCTOR;
      } else if (response.role === 'Passenger') {
        roleId = ROLES.PASSENGER;
      }
      
      const userData = {
        id: response.userID || 0,
        username: response.username,
        fullName: response.fullName,
        role: roleId,
        roleName: response.role,
        permissions: response.permissions || []
      };
      
      // Add bus number for conductor if provided
      if (credentials.role === ROLES.CONDUCTOR && credentials.busNumber) {
        userData.busNumber = credentials.busNumber;
      }
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', response.token);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      
      // Extract error message from API response
      let errorMessage = 'Invalid username or password. Please check your credentials.';
      
      if (error.details) {
        // Error from API service with details
        errorMessage = error.details.message || error.details.error || error.message || errorMessage;
      } else if (error.message) {
        // Check if message contains JSON
        try {
          const jsonMatch = error.message.match(/\{.*\}/);
          if (jsonMatch) {
            const errorData = JSON.parse(jsonMatch[0]);
            errorMessage = errorData.message || errorData.error || errorMessage;
          } else {
            errorMessage = error.message;
          }
        } catch (e) {
          errorMessage = error.message;
        }
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const isRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  const value = {
    user,
    setUser,
    login,
    logout,
    loading,
    hasPermission,
    isRole,
    ROLES
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 